/**
 * components/client-detail/ClientDocumentsTab.tsx
 *
 * Documents tab for the client detail page.
 * Groups documents by catalog name (CRM short label), falling back to raw field label.
 * Provides preview and download per document.
 *
 * Updated Mar 2026: catalogName support; preview modal; PDF conversion; retry buttons.
 * Updated Apr 2026: dossier builder button, embedded mode, fieldId/fieldPosition.
 * Updated Apr 2026: display time alongside date; removed file size.
 * Updated Apr 2026: "Constituer le dossier de pièces" button lifted out to the toolbar
 *   in ClientDetail — this component now accepts onBuildDossier callback and
 *   hasPdfDocs boolean from parent instead of owning the DossierBuilderModal state.
 *   The retry-all button for failed conversions is still handled internally.
 * Updated Apr 2026: compact category headers; filename is plain text (not a link);
 *   Eye/Download always visible at opacity-40, full opacity on hover; icon sizes reduced.
 * Updated Jun 2026: reclassification (move to another category) and inline renaming.
 *   Accepts availableFields prop (document-type fields from the same request) to
 *   populate the "Move to" popover. Uses useDocumentOverride hook for persistence.
 * Updated Jun 2026: MovePopover rendered via React Portal to avoid overflow clipping.
 *   Position calculated from trigger button's bounding rect with viewport-flip logic.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Download, Loader2, AlertCircle, FolderOpen, Image, File, Eye, RefreshCw, FolderInput, Pencil, Check, X } from 'lucide-react';
import Button from '../ui/Button';
import { supabase } from '../../lib/supabase';
import { Z_INDEX } from '../../constants/zIndex';
import { formatDateWithTime } from '../../utils/dateUtils';
import { useDocumentConversion } from '../../hooks/useDocumentConversion';
import { useDocumentOverride } from '../../hooks/useDocumentOverride';
import { useToast } from '../../contexts/ToastContext';
import DocumentPreviewModal from './DocumentPreviewModal';
import type { UploadStatus } from '../../types/database';
import type { DocumentEntry, DossierPiece } from '../../types/dossier';
import { captureError } from '../../utils/errorReporting';

export type { DocumentEntry, DossierPiece };

/** A document-type field available as a reclassification target */
export interface AvailableField {
  fieldId: string;
  requestId: string;
  label: string;
  position: number;
}

interface ClientDocumentsTabProps {
  documents: DocumentEntry[];
  availableFields: AvailableField[];
  onRefresh: () => void;
  embedded?: boolean;
}

interface PreviewTarget {
  storagePath: string;
  filename: string;
  mimeType: string;
}

const getMimeIcon = (mimeType: string, hasPdf: boolean) => {
  if (hasPdf || mimeType === 'application/pdf') return <FileText className="w-4 h-4 text-red-300" />;
  if (mimeType.startsWith('image/')) return <Image className="w-4 h-4 text-primary-300" />;
  return <File className="w-4 h-4 text-neutral-400" />;
};

const StatusPill = ({ status, isRetrying }: { status: UploadStatus; isRetrying: boolean }) => {
  const effectiveStatus = isRetrying ? 'converting' : status;
  if (effectiveStatus === 'completed') return null;
  if (effectiveStatus === 'uploading' || effectiveStatus === 'converting') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-warning-50 text-warning-700 border border-warning-200">
        <Loader2 className="w-3 h-3 animate-spin" />
        {effectiveStatus === 'uploading' ? 'Envoi...' : 'Conversion...'}
      </span>
    );
  }
  if (effectiveStatus === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-error-50 text-error-700 border border-error-200">
        <AlertCircle className="w-3 h-3" />
        Échec
      </span>
    );
  }
  return null;
};

interface MovePopoverProps {
  doc: DocumentEntry;
  availableFields: AvailableField[];
  anchorEl: HTMLElement | null;
  onReclassify: (docId: string, targetFieldId: string) => Promise<boolean>;
  onClose: () => void;
}

function MovePopover({ doc, availableFields, anchorEl, onReclassify, onClose }: MovePopoverProps) {
  const [saving, setSaving] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: -9999, right: 0 });

  const targets = availableFields.filter(
    (f) => f.requestId === doc.requestId && f.fieldId !== doc.fieldId,
  );

  useLayoutEffect(() => {
    const compute = () => {
      if (!anchorEl) return;
      const rect = anchorEl.getBoundingClientRect();
      const popoverH = menuRef.current?.offsetHeight ?? 220;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= popoverH + 8 ? rect.bottom + 4 : rect.top - popoverH - 4;
      setPos({ top, right: window.innerWidth - rect.right });
    };

    compute();

    const handleOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(t) &&
        anchorEl && !anchorEl.contains(t)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [anchorEl, onClose]);

  const handleMove = async (fieldId: string) => {
    setSaving(true);
    await onReclassify(doc.id, fieldId);
    setSaving(false);
    onClose();
  };

  return createPortal(
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: Z_INDEX.POPOVER, width: '15rem' }}
      className="bg-white border border-neutral-200 rounded-xl shadow-soft-lg overflow-hidden"
    >
      {targets.length === 0 ? (
        <p className="px-3 py-3 text-xs text-neutral-400 text-center">Aucune autre catégorie disponible dans cette demande</p>
      ) : (
        <>
          <div className="px-3 py-2 border-b border-neutral-100">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Déplacer vers</p>
          </div>
          <div className="py-1 max-h-48 overflow-y-auto">
            {targets.map((f) => (
              <button
                key={f.fieldId}
                type="button"
                disabled={saving}
                onClick={() => handleMove(f.fieldId)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary-700 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FolderInput className="w-3.5 h-3.5 shrink-0 text-neutral-400" />
                <span className="truncate">{f.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}

interface InlineRenameProps {
  currentName: string;
  onSave: (newName: string) => Promise<boolean>;
  onCancel: () => void;
}

function InlineRename({ currentName, onSave, onCancel }: InlineRenameProps) {
  const [value, setValue] = useState(currentName);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    if (!value.trim() || value.trim() === currentName) { onCancel(); return; }
    setSaving(true);
    await onSave(value.trim());
    setSaving(false);
    onCancel();
  };

  return (
    <div className="flex items-center gap-1 flex-1 min-w-0">
      <input
        ref={inputRef}
        autoFocus
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') onCancel();
        }}
        className="flex-1 min-w-0 text-sm text-neutral-800 bg-white border border-primary-300 rounded-lg px-2 py-0.5 outline-none focus:ring-2 focus:ring-primary-100"
        disabled={saving}
      />
      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="p-1 rounded text-success-600 hover:bg-success-50 disabled:opacity-50 transition-all"
        title="Valider"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        className="p-1 rounded text-neutral-400 hover:bg-neutral-100 transition-all"
        title="Annuler"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function ClientDocumentsTab({ documents, availableFields, onRefresh, embedded = false }: ClientDocumentsTabProps) {
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(null);
  const [openMoveDocId, setOpenMoveDocId] = useState<string | null>(null);
  const [renamingDocId, setRenamingDocId] = useState<string | null>(null);
  // Stores the DOM element of the currently-open move trigger button for portal positioning
  const moveAnchorRef = useRef<HTMLElement | null>(null);
  const { showToast } = useToast();
  const {
    retryingDocs,
    retryConversion,
    retryAllFailed,
  } = useDocumentConversion(onRefresh);
  const { reclassifyDocument, renameDocument } = useDocumentOverride(onRefresh);

  const downloadDocument = async (storagePath: string, filename: string) => {
    const { data, error } = await supabase.storage
      .from('request-documents')
      .download(storagePath);
    if (error || !data) {
      console.error('Document download error:', error);
      if (error) {
        captureError(error, { component: 'ClientDocumentsTab', action: 'downloadDocument', storagePath });
      } else {
        captureError(new Error('Empty download response'), { component: 'ClientDocumentsTab', action: 'downloadDocument', storagePath });
      }
      showToast('Impossible de télécharger le document', 'error');
      return;
    }
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 rounded-2xl mb-4">
          <FolderOpen className="w-7 h-7 text-neutral-400" />
        </div>
        <p className="text-neutral-600 font-medium mb-1">Aucun document reçu</p>
        <p className="text-sm text-neutral-400">
          Les documents envoyés par le client apparaîtront ici
        </p>
      </div>
    );
  }

  const categories = documents.reduce<Record<string, { displayName: string; docs: DocumentEntry[] }>>((acc, doc) => {
    const displayName = doc.fieldLabel;
    if (!acc[displayName]) acc[displayName] = { displayName, docs: [] };
    acc[displayName].docs.push(doc);
    return acc;
  }, {});

  const failedDocs = documents.filter((d) => d.uploadStatus === 'failed');
  const hasMultipleFailed = failedDocs.length > 1;

  // Check if reclassification is possible (at least 2 distinct document fields across all requests)
  const canReclassify = availableFields.length >= 2 || (availableFields.length >= 1 && documents.some((d) => d.requestId === availableFields[0]?.requestId));

  const handleOpenMove = (e: React.MouseEvent<HTMLButtonElement>, docId: string) => {
    if (openMoveDocId === docId) {
      moveAnchorRef.current = null;
      setOpenMoveDocId(null);
    } else {
      moveAnchorRef.current = e.currentTarget;
      setOpenMoveDocId(docId);
    }
  };

  const handleCloseMove = () => {
    moveAnchorRef.current = null;
    setOpenMoveDocId(null);
  };

  return (
    <>
      <div className="space-y-3">
        {hasMultipleFailed && (
          <div className="flex">
            <Button
              variant="secondary"
              onClick={() => retryAllFailed(failedDocs)}
              disabled={retryingDocs.size > 0}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${retryingDocs.size > 0 ? 'animate-spin' : ''}`} />
              Réessayer les échecs ({failedDocs.length})
            </Button>
          </div>
        )}

        {Object.entries(categories).map(([key, { displayName, docs }]) => (
          <div key={key} className={embedded ? 'rounded-xl border border-neutral-100 overflow-hidden' : 'bg-white rounded-2xl border border-neutral-200 overflow-hidden shadow-soft'}>
            {/* Category header — compact, label-style separator */}
            <div className="flex items-center gap-2.5 px-4 py-2.5 bg-neutral-50 border-b border-neutral-100">
              <div className="flex items-center justify-center w-7 h-7 bg-primary-100 rounded-lg flex-shrink-0">
                <FolderOpen className="w-3.5 h-3.5 text-primary-600" />
              </div>
              <h3 className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500 leading-none">{displayName}</h3>
            </div>

            {/* Document rows */}
            <div className="divide-y divide-neutral-50">
              {docs.map((doc) => {
                const isRetrying = retryingDocs.has(doc.id);
                const previewPath = doc.pdfPath ?? doc.storagePath;
                const previewMime = doc.pdfPath ? 'application/pdf' : doc.mimeType;
                const docDisplayName = doc.displayFilename ?? doc.originalFilename;
                const isRenamingThis = renamingDocId === doc.id;
                const isMoveOpenForThis = openMoveDocId === doc.id;

                return (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors group"
                  >
                    {/* File type icon */}
                    <div className="flex-shrink-0 opacity-70">
                      {getMimeIcon(doc.mimeType, !!doc.pdfPath)}
                    </div>

                    {/* Filename + meta */}
                    <div className="flex-1 min-w-0">
                      {isRenamingThis ? (
                        <InlineRename
                          currentName={docDisplayName}
                          onSave={(newName) => renameDocument(doc.id, newName)}
                          onCancel={() => setRenamingDocId(null)}
                        />
                      ) : (
                        <div className="flex items-center gap-2 min-w-0">
                          <p
                            className="text-sm text-neutral-800 truncate flex-1 min-w-0"
                            title={docDisplayName}
                          >
                            {docDisplayName}
                          </p>
                          <StatusPill status={doc.uploadStatus} isRetrying={isRetrying} />
                        </div>
                      )}
                      {!isRenamingThis && (
                        <p className="text-xs text-neutral-400 mt-0.5">
                          {formatDateWithTime(doc.createdAt)}
                        </p>
                      )}
                    </div>

                    {/* Action buttons */}
                    {!isRenamingThis && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {doc.uploadStatus === 'failed' && !isRetrying && (
                          <button
                            type="button"
                            onClick={() => retryConversion(doc.id, doc.requestId)}
                            className="p-2 rounded-lg text-error-500 hover:text-error-700 hover:bg-error-50 transition-all"
                            aria-label="Réessayer la conversion"
                            title="Réessayer la conversion"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        )}

                        {/* Rename button — always available regardless of upload status */}
                        <button
                          type="button"
                          onClick={() => setRenamingDocId(doc.id)}
                          className="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 transition-all opacity-40 group-hover:opacity-100 max-md:opacity-100"
                          aria-label="Renommer ce document"
                          title="Renommer"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>

                        {/* Move to another category button — portal-positioned to avoid overflow clipping */}
                        {canReclassify && (
                          <button
                            type="button"
                            onClick={(e) => handleOpenMove(e, doc.id)}
                            className={`p-2 rounded-lg transition-all opacity-40 group-hover:opacity-100 max-md:opacity-100 ${
                              isMoveOpenForThis
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-neutral-400 hover:text-primary-600 hover:bg-primary-50'
                            }`}
                            aria-label="Déplacer vers une autre catégorie"
                            title="Déplacer vers"
                          >
                            <FolderInput className="w-4 h-4" />
                          </button>
                        )}

                        {doc.uploadStatus === 'completed' && (
                          <div className="flex items-center gap-1 opacity-40 group-hover:opacity-100 max-md:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => setPreviewTarget({ storagePath: previewPath, filename: docDisplayName, mimeType: previewMime })}
                              className="p-2 rounded-lg text-neutral-500 hover:text-primary-600 hover:bg-primary-50 transition-all"
                              aria-label={`Aperçu de ${docDisplayName}`}
                              title="Aperçu"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => downloadDocument(previewPath, docDisplayName)}
                              className="p-2 rounded-lg text-neutral-500 hover:text-primary-600 hover:bg-primary-50 transition-all"
                              aria-label={`Télécharger ${docDisplayName}`}
                              title="Télécharger"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Portal popover rendered outside overflow containers */}
      {openMoveDocId && (() => {
        const doc = documents.find((d) => d.id === openMoveDocId);
        if (!doc) return null;
        return (
          <MovePopover
            doc={doc}
            availableFields={availableFields}
            anchorEl={moveAnchorRef.current}
            onReclassify={reclassifyDocument}
            onClose={handleCloseMove}
          />
        );
      })()}

      {previewTarget && (
        <DocumentPreviewModal
          storagePath={previewTarget.storagePath}
          filename={previewTarget.filename}
          mimeType={previewTarget.mimeType}
          onClose={() => setPreviewTarget(null)}
        />
      )}
    </>
  );
}
